import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORIES = ["Lighting", "Recovery", "Storage", "Armor", "Wheels & Tires", "Exterior", "Uncategorized"];
const PAGE_SIZE = 20;

const emptyForm = {
    slug: "", sku: "", name: "", category: "Lighting",
    price: 0, fitment: "Universal", blurb: "", description: "",
    images: "", stock: 0, badge: "",
};

function StockPill({ p }) {
    const status = p.stock_status || (p.stock > 10 ? "In Stock" : p.stock > 0 ? "Low Stock" : "Out of Stock");
    const color = status === "In Stock"
        ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
        : status === "Low Stock"
            ? "border-[hsl(var(--rust))] text-[hsl(var(--rust))]"
            : status === "Out of Stock"
                ? "border-[hsl(var(--danger))] text-[hsl(var(--danger))]"
                : "border-[hsl(var(--ink-soft))] text-[hsl(var(--ink-soft))]";
    return (
        <span className={`inline-block whitespace-nowrap border px-2 py-0.5 font-mono-brand text-[10px] uppercase tracking-wider ${color}`}>
            ● {status}{p.stock ? ` · ${p.stock}` : ""}
        </span>
    );
}

function BadgePill({ b }) {
    if (!b) return <span className="font-mono-brand text-[10px] text-[hsl(var(--ink-soft))]">No Badge</span>;
    const styles = b === "SALE"
        ? "bg-[hsl(var(--rust))] text-[hsl(var(--dust-50))]"
        : b === "NEW"
            ? "bg-[hsl(var(--ink))] text-[hsl(var(--primary))]"
            : "bg-[hsl(var(--dust-300))] text-[hsl(var(--dust-50))]";
    return <span className={`inline-block px-2 py-0.5 font-mono-brand text-[10px] uppercase tracking-wider ${styles}`}>{b}</span>;
}

function SyncSummary({ s, onClose }) {
    if (!s) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <div className="max-h-[85vh] max-w-2xl overflow-y-auto border-2 border-[hsl(var(--ink))] bg-card p-6" onClick={(e) => e.stopPropagation()} data-testid="sync-summary-modal">
                <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Shopify sync</div>
                <h2 className="mt-1 font-display text-3xl uppercase italic text-[hsl(var(--ink))]">Sync complete</h2>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
                    {[
                        ["Discovered", s.discovered, "text-[hsl(var(--ink))]"],
                        ["Created", s.created, "text-[hsl(var(--success))]"],
                        ["Updated", s.updated, "text-[hsl(var(--forest))]"],
                        ["Skipped", s.skipped, "text-[hsl(var(--ink-soft))]"],
                        ["Failed", s.failed, "text-[hsl(var(--danger))]"],
                    ].map(([l, v, c]) => (
                        <div key={l} className="border border-[hsl(var(--dust-300))] p-3">
                            <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{l}</div>
                            <div className={`mt-1 font-display text-3xl ${c}`}>{v}</div>
                        </div>
                    ))}
                </div>
                {s.flagged_count > 0 && (
                    <div className="mt-6">
                        <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--rust))]">⚠ {s.flagged_count} products flagged for review</div>
                        <ul className="mt-2 max-h-48 overflow-y-auto border border-[hsl(var(--dust-300))] p-3 font-mono-brand text-xs">
                            {(s.flagged || []).map((f, i) => (
                                <li key={i} className="border-b border-[hsl(var(--dust-300))] py-1 last:border-b-0">
                                    <span className="text-[hsl(var(--ink))]">{f.name}</span>
                                    <span className="ml-2 text-[hsl(var(--ink-soft))]">[{f.sku}]</span>
                                    <span className="ml-2 text-[hsl(var(--danger))]">{f.flags.join(", ")}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {s.errors && s.errors.length > 0 && (
                    <div className="mt-4 border border-[hsl(var(--danger))] p-3 font-mono-brand text-xs text-[hsl(var(--danger))]">
                        {s.errors.map((e, i) => <div key={i}>Error: {e.error}</div>)}
                    </div>
                )}
                <div className="mt-6 font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    Source: {s.source} · {s.duration_ms} ms
                </div>
                <div className="mt-6">
                    <button onClick={onClose} className="btn-primary" data-testid="sync-summary-close">Close</button>
                </div>
            </div>
        </div>
    );
}

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [q, setQ] = useState("");
    const [uploading, setUploading] = useState(false);
    const [sortKey, setSortKey] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [syncSummary, setSyncSummary] = useState(null);
    const [overwrite, setOverwrite] = useState(false);

    const load = () => api.get("/products", { params: { limit: 500, ...(q ? { q } : {}) } }).then(({ data }) => setProducts(data.items));
    useEffect(() => { load(); setPage(0); }, [q]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((prev) => ({ ...prev, images: prev.images ? `${prev.images}\n${data.url}` : data.url }));
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const startCreate = () => { setForm(emptyForm); setCreating(true); setEditing(null); };
    const startEdit = (p) => {
        setForm({
            slug: p.slug, sku: p.sku, name: p.name, category: p.category,
            price: p.price, fitment: p.fitment, blurb: p.blurb || "",
            description: p.description || "", images: (p.images || []).join("\n"),
            stock: p.stock, badge: p.badge || "",
        });
        setEditing(p.slug);
        setCreating(false);
    };
    const cancel = () => { setEditing(null); setCreating(false); };

    const save = async (e) => {
        e.preventDefault();
        const trimmed = (v) => (typeof v === "string" ? v.trim() : v);
        const sku = trimmed(form.sku);
        if (!sku) { toast.error("SKU is required"); return; }
        const price = parseFloat(form.price);
        if (isNaN(price) || price < 0) { toast.error("Price must be a non-negative number"); return; }
        const stock = parseInt(form.stock, 10);
        if (isNaN(stock) || stock < 0) { toast.error("Stock must be a non-negative number"); return; }

        const payload = {
            slug: trimmed(form.slug),
            sku, name: trimmed(form.name),
            category: form.category || "Uncategorized",
            price,
            fitment: trimmed(form.fitment) || "Universal",
            blurb: form.blurb,
            description: form.description,
            images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
            stock,
            badge: form.badge || null,
            specs: [],
        };
        try {
            if (creating) {
                await api.post("/admin/products", payload);
                toast.success("Product created");
            } else {
                await api.put(`/admin/products/${editing}`, payload);
                toast.success("Product updated (protected from re-sync)");
            }
            cancel(); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}" (${p.sku})? This cannot be undone.`)) return;
        try { await api.delete(`/admin/products/${p.slug}`); toast.success("Deleted"); load(); }
        catch { toast.error("Failed"); }
    };

    const runSync = async () => {
        const confirmMsg = overwrite
            ? "Sync will OVERWRITE any manual admin edits with fresh data from Shopify. Continue?"
            : "Sync will pull the latest catalog from urbanoff-road.com. Any admin-edited products will be preserved. Continue?";
        if (!window.confirm(confirmMsg)) return;
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/shopify/sync", { overwrite_edits: overwrite });
            setSyncSummary(data);
            toast.success(`Synced ${data.created + data.updated} products from Shopify`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Sync failed");
        } finally { setSyncing(false); }
    };

    const sorted = useMemo(() => {
        const arr = [...products];
        arr.sort((a, b) => {
            let va = a[sortKey];
            let vb = b[sortKey];
            if (typeof va === "string") { va = va.toLowerCase(); vb = (vb || "").toLowerCase(); }
            if (va == null) va = "";
            if (vb == null) vb = "";
            if (va < vb) return sortDir === "asc" ? -1 : 1;
            if (va > vb) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [products, sortKey, sortDir]);

    const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageItems = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    const toggleSort = (k) => {
        if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else { setSortKey(k); setSortDir("asc"); }
    };

    return (
        <div data-testid="admin-products">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Catalog</div>
                    <h1 className="mt-1 font-display text-4xl uppercase italic text-[hsl(var(--ink))]">Products</h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                        <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="h-3 w-3" data-testid="sync-overwrite-toggle" />
                        Overwrite manual edits
                    </label>
                    <button onClick={runSync} disabled={syncing} className="border-2 border-[hsl(var(--ink))] bg-[hsl(var(--rust))] px-4 py-2 font-heading text-xs font-bold uppercase tracking-wider text-[hsl(var(--dust-50))] hover:opacity-90 disabled:opacity-60" data-testid="sync-shopify-btn">
                        {syncing ? "Syncing…" : "↓ Sync from Shopify"}
                    </button>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, name, fitment" className="border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm focus:outline-none" data-testid="admin-products-search" />
                    <button onClick={startCreate} className="btn-primary" data-testid="admin-products-new">+ New</button>
                </div>
            </div>

            <div className="mt-3 font-mono-brand text-xs text-[hsl(var(--ink-soft))]">
                {sorted.length} products · Sortable columns · Page {page + 1} of {pages}
            </div>

            {(editing || creating) && (
                <form onSubmit={save} className="mt-6 grid gap-4 border-2 border-[hsl(var(--ink))] bg-card p-6 md:grid-cols-2" data-testid="admin-products-form">
                    {[["slug", "Slug (url)"], ["sku", "SKU"], ["name", "Name"], ["fitment", "Fitment"], ["price", "Price"], ["stock", "Stock"]].map(([k, l]) => (
                        <label key={k} className="text-sm">
                            <div className="font-heading text-xs uppercase tracking-wider">{l}</div>
                            <input required type={k === "price" || k === "stock" ? "number" : "text"} step={k === "price" ? "0.01" : undefined} min={k === "price" || k === "stock" ? "0" : undefined} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} disabled={k === "slug" && !!editing} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm disabled:opacity-60" data-testid={`admin-product-${k}`} />
                        </label>
                    ))}
                    <label className="text-sm">
                        <div className="font-heading text-xs uppercase tracking-wider">Category</div>
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm">
                            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                    </label>
                    <label className="text-sm">
                        <div className="font-heading text-xs uppercase tracking-wider">Badge</div>
                        <select value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm">
                            <option value="">— No Badge —</option>
                            <option value="NEW">NEW</option>
                            <option value="SALE">SALE</option>
                            <option value="FEATURED">FEATURED</option>
                            <option value="BEST SELLER">BEST SELLER</option>
                        </select>
                    </label>
                    <label className="text-sm md:col-span-2">
                        <div className="font-heading text-xs uppercase tracking-wider">Blurb</div>
                        <textarea rows={2} value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm" />
                    </label>
                    <label className="text-sm md:col-span-2">
                        <div className="font-heading text-xs uppercase tracking-wider">Description (HTML allowed)</div>
                        <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm" />
                    </label>
                    <label className="text-sm md:col-span-2">
                        <div className="flex items-center justify-between">
                            <div className="font-heading text-xs uppercase tracking-wider">Images (one URL per line)</div>
                            <label className="cursor-pointer border border-[hsl(var(--ink))] px-3 py-1 font-heading text-[10px] uppercase tracking-wider hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--dust-50))]">
                                {uploading ? "Uploading…" : "+ Upload image"}
                                <input type="file" accept="image/*" onChange={onUpload} disabled={uploading} className="hidden" />
                            </label>
                        </div>
                        <textarea rows={3} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm" />
                        {form.images && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {form.images.split("\n").filter(Boolean).slice(0, 8).map((url, i) => (
                                    <img key={url + i} src={url} alt="" className="h-16 w-16 border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] object-contain" />
                                ))}
                            </div>
                        )}
                    </label>
                    <div className="md:col-span-2 flex gap-3">
                        <button type="submit" className="btn-primary" data-testid="admin-product-save">Save</button>
                        <button type="button" onClick={cancel} className="btn-ghost">Cancel</button>
                    </div>
                </form>
            )}

            <div className="mt-6 overflow-x-auto border-2 border-[hsl(var(--ink))] bg-card">
                <table className="w-full font-mono-brand text-xs">
                    <thead className="bg-[hsl(var(--ink))] text-[hsl(var(--dust-50))]">
                        <tr>
                            {[
                                ["sku", "SKU"],
                                ["name", "Name"],
                                ["category", "Category"],
                                ["price", "Price"],
                                ["stock", "Stock"],
                                ["badge", "Badge"],
                                [null, "Actions"],
                            ].map(([k, l]) => (
                                <th key={l} className="px-3 py-2 text-left font-heading text-xs uppercase tracking-wider">
                                    {k ? (
                                        <button onClick={() => toggleSort(k)} className="hover:text-[hsl(var(--primary))]" data-testid={`sort-${k}`}>
                                            {l} {sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : ""}
                                        </button>
                                    ) : l}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.length === 0 && (
                            <tr><td colSpan={7} className="p-6 text-center text-[hsl(var(--ink-soft))]">No products match.</td></tr>
                        )}
                        {pageItems.map((p) => (
                            <tr key={p.slug} className={`border-t border-[hsl(var(--dust-300))] ${p.admin_edited ? "bg-[hsl(var(--primary))]/10" : ""}`} data-testid={`admin-product-row-${p.slug}`}>
                                <td className="px-3 py-2 align-top">
                                    <div className="font-mono-brand text-xs">{p.sku}</div>
                                    {p.admin_edited && <div className="mt-0.5 font-mono-brand text-[9px] uppercase tracking-wider text-[hsl(var(--forest))]">● manual</div>}
                                    {p.flags && p.flags.length > 0 && <div className="mt-0.5 font-mono-brand text-[9px] uppercase tracking-wider text-[hsl(var(--danger))]">⚠ {p.flags.join(", ")}</div>}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <div className="flex items-start gap-2">
                                        {p.images?.[0] && <img src={p.images[0]} alt="" className="h-12 w-12 flex-shrink-0 border border-[hsl(var(--dust-300))] bg-[hsl(var(--dust-50))] object-contain" />}
                                        <div className="font-sans">{p.name}</div>
                                    </div>
                                </td>
                                <td className="px-3 py-2 align-top">{p.category || "Uncategorized"}</td>
                                <td className="px-3 py-2 align-top font-mono-brand">${Number(p.price || 0).toFixed(2)}</td>
                                <td className="px-3 py-2 align-top"><StockPill p={p} /></td>
                                <td className="px-3 py-2 align-top"><BadgePill b={p.badge} /></td>
                                <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                                    <button onClick={() => startEdit(p)} className="mr-3 underline underline-offset-4 hover:text-[hsl(var(--forest))]" data-testid={`admin-edit-${p.slug}`}>Edit</button>
                                    <button onClick={() => remove(p)} className="underline underline-offset-4 hover:text-[hsl(var(--danger))]" data-testid={`admin-delete-${p.slug}`}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {pages > 1 && (
                <div className="mt-4 flex items-center justify-between font-mono-brand text-xs">
                    <button disabled={page === 0} onClick={() => setPage(page - 1)} className="border border-[hsl(var(--ink))] px-3 py-1 uppercase disabled:opacity-30" data-testid="page-prev">← Prev</button>
                    <span className="text-[hsl(var(--ink-soft))]">Page {page + 1} / {pages} · showing {pageItems.length} of {sorted.length}</span>
                    <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="border border-[hsl(var(--ink))] px-3 py-1 uppercase disabled:opacity-30" data-testid="page-next">Next →</button>
                </div>
            )}

            <SyncSummary s={syncSummary} onClose={() => setSyncSummary(null)} />
        </div>
    );
}
